import React, {
  useRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { t } from 'i18next'
import ReactQuill from 'react-quill'
import { useSelector } from 'react-redux'
import infoCircle from 'Assets/Images/Generals/infoCircle.svg'
import 'react-quill/dist/quill.snow.css'
import './AdditionalServiceDetailsEditor.css'

/**
 * AdditionalServiceDetailsEditor
 *
 * A rich-text editor component for entering additional service notes,
 * based on ReactQuill. Enforces a 5000-character limit, including both HTML and plain text.
 *
 * Features:
 * - Initializes with content from the Redux store (`ServiceNote`)
 * - Enforces a max length of 5000 characters, accounting for both HTML tags and text
 * - Truncates pasted content safely without breaking HTML tags
 * - Maintains and restores cursor position on truncation
 * - Supports both read-only and editable modes
 * - Provides access to content and editor methods via ref
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.readOnly=false] - Whether the editor should be in read-only mode
 * @param {React.Ref} ref - Exposes editor methods:
 *   - getEditor(): Quill instance
 *   - getContent(): Current HTML content
 *   - setContent(content: string): Set content, enforcing max length
 *
 * @returns {JSX.Element|null} Returns the editor UI if `IsService` is true, otherwise null
 */
export const AdditionalServiceDetailsEditor = forwardRef(
  ({ readOnly = false }, ref) => {
    const quillRef = useRef(null)
    const { IsService, ServiceNote } = useSelector(
      state => state.cotizacion.QuoteHeader
    )

    const [localContent, setLocalContent] = useState('')
    const isInitialized = useRef(false)
    const lastValidContent = useRef('')

    const MAX_LENGTH = 5000

    const readOnlyModules = { toolbar: false }

    const modules = {
      toolbar: [
        [
          'bold',
          'italic',
          { list: 'bullet' },
          { align: '' },
          { align: 'center' },
          { align: 'right' },
        ],
      ],
    }

    const getHtmlLength = quill => {
      if (!quill) return 0
      const html = quill.root.innerHTML
      return html.length
    }

    // Function to safely truncate HTML, counting ALL characters
    const truncateHtmlSafely = (html, maxChars) => {
      if (html.length <= maxChars) {
        return html
      }

      let result = ''
      let charCount = 0
      let tagBuffer = ''
      let inTag = false

      for (let i = 0; i < html.length; i++) {
        const char = html[i]

        if (char === '<') {
          inTag = true
          tagBuffer = '<'
          continue
        }

        if (inTag) {
          tagBuffer += char
          if (char === '>') {
            inTag = false
            // Add the full HTML tag without splitting it, but still count its characters toward the total limit.
            if (charCount + tagBuffer.length <= maxChars) {
              result += tagBuffer
              charCount += tagBuffer.length
            } else {
              // If adding this tag exceeds the limit, stop.
              break
            }
            tagBuffer = ''
          }
          continue
        }

        // Plain text characters (excluding HTML tags)
        if (charCount < maxChars) {
          result += char
          charCount++
        } else {
          break
        }
      }

      return result
    }

    // Initialize local content
    useEffect(() => {
      if (!isInitialized.current && ServiceNote) {
        setLocalContent(ServiceNote)
        lastValidContent.current = ServiceNote
        isInitialized.current = true
      } else if (!isInitialized.current && !ServiceNote) {
        setLocalContent('')
        lastValidContent.current = ''
        isInitialized.current = true
      }
    }, [ServiceNote])

    // Paste control
    useEffect(() => {
      const editor = quillRef.current?.getEditor()
      if (!editor) return

      const handlePaste = e => {
        e.preventDefault()

        const quill = quillRef.current?.getEditor()
        if (!quill) return

        const clipboardHTML = e.clipboardData?.getData('text/html') || ''
        const clipboardText = e.clipboardData?.getData('text/plain') || ''

        // Get the current content in HTML
        const currentHTML = quill.root.innerHTML
        const currentLength = currentHTML.length
        const availableSpace = MAX_LENGTH - currentLength

        if (availableSpace <= 0) return

        let insertHTML = ''

        // Prioritize HTML if available
        if (clipboardHTML) {
          if (clipboardHTML.length <= availableSpace) {
            insertHTML = clipboardHTML
          } else {
            // Safely truncate while keeping tags intact
            insertHTML = truncateHtmlSafely(clipboardHTML, availableSpace)
          }
        } else if (clipboardText) {
          if (clipboardText.length <= availableSpace) {
            insertHTML = clipboardText
          } else {
            insertHTML = clipboardText.substring(0, availableSpace)
          }
        }

        if (!insertHTML) return

        const selection = quill.getSelection()
        const insertIndex = selection?.index ?? quill.getLength()

        // Determine if content is HTML or plain text
        const isHTML = insertHTML.includes('<')

        if (isHTML) {
          quill.clipboard.dangerouslyPasteHTML(insertIndex, insertHTML)
        } else {
          quill.insertText(insertIndex, insertHTML, 'user')
        }

        // Update content
        setTimeout(() => {
          const updatedContent = quill.root.innerHTML
          const updatedLength = updatedContent.length

          // If the resulting content exceeds the limit, truncate it
          if (updatedLength > MAX_LENGTH) {
            const truncatedHTML = truncateHtmlSafely(updatedContent, MAX_LENGTH)
            quill.clipboard.dangerouslyPasteHTML(truncatedHTML)
            lastValidContent.current = truncatedHTML
            setLocalContent(truncatedHTML)
          } else {
            lastValidContent.current = updatedContent
            setLocalContent(updatedContent)
          }
        }, 0)
      }

      const root = editor.root
      root.addEventListener('paste', handlePaste, true)
      return () => root.removeEventListener('paste', handlePaste, true)
    }, [])

    const handleChange = (content, delta, source) => {
      const quill = quillRef.current?.getEditor()
      if (!quill || source !== 'user') return

      const currentLength = getHtmlLength(quill)

      if (currentLength > MAX_LENGTH) {
        const truncatedContent = truncateHtmlSafely(content, MAX_LENGTH)
        const currentSelection = quill.getSelection()

        // Revert to the truncated content
        quill.clipboard.dangerouslyPasteHTML(truncatedContent)
        lastValidContent.current = truncatedContent
        setLocalContent(truncatedContent)

        // Restore cursor position
        if (currentSelection) {
          setTimeout(() => {
            quill.setSelection(
              Math.min(currentSelection.index, MAX_LENGTH),
              currentSelection.length || 0
            )
          }, 0)
        }

        return
      }

      // If within the limit, save the new content
      lastValidContent.current = content
      setLocalContent(content)
    }

    useImperativeHandle(ref, () => ({
      getEditor: () => quillRef.current?.getEditor(),
      getContent: () => localContent,
      setContent: content => {
        const truncatedContent = truncateHtmlSafely(content, MAX_LENGTH)
        setLocalContent(truncatedContent)
        lastValidContent.current = truncatedContent
        const editor = quillRef.current?.getEditor()
        if (editor) {
          editor.root.innerHTML = truncatedContent
        }
      },
    }))

    if (!IsService) return null

    return (
      <>
        <div
          className="additional-service-details"
          data-testid="container-additional-service-details">
          <h3
            className="additional-service-details-title"
            data-testid="title-service-details">
            {t('ADDITIONAL_SERVICE_DETAILS_EDITOR.TITLE')}
          </h3>
          <p
            className="additional-service-details-description"
            data-testid="text-service-description">
            {t('ADDITIONAL_SERVICE_DETAILS_EDITOR.DESCRIPTION')}
          </p>

          <ReactQuill
            theme="snow"
            value={localContent}
            ref={quillRef}
            onChange={handleChange}
            readOnly={readOnly}
            modules={readOnly ? readOnlyModules : modules}
            className="additional-service-details-editor"
            placeholder={t('ADDITIONAL_SERVICE_DETAILS_EDITOR.PLACEHOLDER')}
            data-testid="input-service-editor"
          />
        </div>

        <div
          className="additional-service-details-warning"
          data-testid="container-service-warning">
          <img
            src={infoCircle}
            alt="information icon"
            className="additional-service-details-warning-icon"
            data-testid="icon-service-info"
          />
          <p
            className="additional-service-details-warning-text"
            data-testid="text-service-warning">
            {t('ADDITIONAL_SERVICE_DETAILS_EDITOR.WARNING_TEXT')}
          </p>
        </div>
      </>
    )
  }
)

AdditionalServiceDetailsEditor.displayName = 'AdditionalServiceDetailsEditor'
